import { z } from 'zod'
import type { AITool } from './types'
// import type { Routine } from '../../types' // Currently unused
import { RoutineService } from '../../services/routineService'
import { exerciseProcessor } from '../../services/exerciseProcessor'
import { ANATOMICAL_MUSCLE_ORDER } from '../../utils'

// No parameters needed for this tool
const parametersSchema = z.object({})

interface DayInfo {
  routineId: string
  routineName: string
  exercises: number
  totalSets: number
  muscleGroups: string[]
}

export const getCurrentSplit: AITool = {
  name: 'getCurrentSplit',
  description: 'Get current training split configuration.',
  parameters: parametersSchema,
  
  execute: async (args, context) => {
    const { workoutData, userSettings, routines } = context
    
    // Check if user has API key configured (routines only available with API)
    const isApiConfigured = userSettings?.settings?.dataSource === 'api' && 
                           workoutData.workouts?.length > 0
    
    if (!isApiConfigured) {
      return {
        summary: 'Training split information requires Hevy API configuration.',
        weeklySplit: {},
        muscleVolume: [],
        statistics: {
          trainingDays: 0,
          restDays: 7,
          totalWeeklySets: 0,
          includesSecondaryMovers: false
        },
        isConfigured: false,
        requiresApiKey: true
      }
    }
    
    // Check if routines are available
    if (!routines || routines.length === 0) {
      return {
        summary: 'No routines found. Create routines in Hevy to configure your training split.',
        weeklySplit: {},
        muscleVolume: [],
        statistics: {
          trainingDays: 0,
          restDays: 7,
          totalWeeklySets: 0,
          includesSecondaryMovers: false
        },
        isConfigured: false,
        requiresApiKey: true
      }
    }
    
    // Get weekly split and routines
    const weeklySplit = userSettings?.weeklySplit || {}
    const routineEnabled = userSettings?.routineEnabled || false
    const includeSecondaryMovers = userSettings?.setsWeekEnabled || false
    const cycleMode = userSettings?.weeklySplit?.cycle_mode || false
    
    // Process each day of the week
    const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const
    const processedSplit: Record<string, DayInfo[] | null> = {}
    let trainingDays = 0
    let totalWeeklySets = 0
    
    // Since we can't fetch routines directly in the tool (they come from a separate API),
    // we'll work with what we have from the weekly split configuration
    daysOfWeek.forEach(day => {
      const dayRoutines = weeklySplit[day]
      if (dayRoutines && dayRoutines.length > 0) {
        trainingDays++
        
        dayRoutines.forEach(routineId => {
          const routine = routines.find(r => r.id === routineId)
          
          if (routine) {
            // Count total sets excluding empty sets
            const totalSets = routine.exercises.reduce(
              (sum, ex) => sum + ex.sets.filter(s => s.type !== 'empty').length,
              0
            )
            
            // Extract unique muscle groups from exercises
            const muscleGroups = new Set<string>()
            routine.exercises.forEach(exercise => {
              const contributions = exerciseProcessor.getMuscleContributionsFromExercise(
                exercise.title,
              exercise.exercise_template_id,
              includeSecondaryMovers
            )
            contributions.forEach(({ muscleGroup }) => muscleGroups.add(muscleGroup))
          })
          
            if (!processedSplit[day]) {
              processedSplit[day] = []
            }
            
            processedSplit[day].push({
              routineId,
              routineName: routine.title,
              exercises: routine.exercises.length,
              totalSets,
              muscleGroups: Array.from(muscleGroups).sort()
            })
            
            totalWeeklySets += totalSets
          } else {
            // Routine not found
            if (!processedSplit[day]) {
              processedSplit[day] = []
            }
            
            processedSplit[day].push({
              routineId,
              routineName: 'Unknown Routine',
              exercises: 0,
              totalSets: 0,
              muscleGroups: []
            })
          }
        })
      } else {
        processedSplit[day] = null // Rest day
      }
    })
    
    // Calculate muscle volume using the same method as the widget
    const muscleVolume = RoutineService.calculateMuscleVolume(routines, weeklySplit, includeSecondaryMovers)
    
    // Sort by anatomical order and calculate percentages
    const sortedMuscleVolume = ANATOMICAL_MUSCLE_ORDER.map(muscleGroup => {
      const volume = muscleVolume.find(v => v.muscleGroup === muscleGroup)
      if (!volume) {
        return {
          muscleGroup,
          weeklySetCount: 0,
          percentageOfMax: 0,
          dailyBreakdown: {},
          exercises: []
        }
      }
      return volume
    })
    
    const maxVolume = Math.max(...sortedMuscleVolume.map(v => v.weeklySetCount), 1)
    const muscleVolumeWithPercentages = sortedMuscleVolume.map(volume => ({
      ...volume,
      percentageOfMax: volume.weeklySetCount > 0 
        ? Math.round((volume.weeklySetCount / maxVolume) * 100)
        : 0
    }))
    
    // Calculate normalized weekly sets for cycle mode
    let normalizedWeeklySets = totalWeeklySets
    if (cycleMode && trainingDays > 0) {
      const cycleFrequency = 7 / trainingDays
      normalizedWeeklySets = Math.round(totalWeeklySets * cycleFrequency)
    }
    
    // Build summary
    const summary = trainingDays > 0
      ? `Training ${cycleMode ? 'cycle' : 'split'}: ${trainingDays} day${trainingDays > 1 ? 's' : ''} ${cycleMode ? 'cycle' : 'per week'}${routineEnabled ? ' with routine configuration enabled' : ''}. ${normalizedWeeklySets} total sets per week${cycleMode ? ` (${totalWeeklySets} sets per cycle × ${(7/trainingDays).toFixed(2)} cycles/week)` : ''}.`
      : 'No training split configured. Use the routine configuration widget to set up your weekly training schedule.'
    
    return {
      summary,
      
      // Weekly split overview
      weeklySplit: processedSplit,
      
      // Muscle volume analysis (would be populated with routine data)
      muscleVolume: muscleVolumeWithPercentages.filter(v => v.weeklySetCount > 0),
      
      // Statistics
      statistics: {
        trainingDays,
        restDays: cycleMode ? 0 : 7 - trainingDays,
        totalWeeklySets: normalizedWeeklySets,
        cycleMode,
        cycleLength: cycleMode ? trainingDays : undefined,
        weeklyFrequency: cycleMode && trainingDays > 0 ? 7 / trainingDays : trainingDays,
        includesSecondaryMovers: includeSecondaryMovers,
        routineEnabled
      },
      
      // Configuration status
      isConfigured: trainingDays > 0,
      requiresApiKey: true
    }
  }
}